/**
 * L1J Web Client - Equipment Panel
 *
 * 현재 장착 중인 아이템 확인 창 (화면 좌측, 기본 숨김).
 *
 * 서버는 장착 슬롯 목록을 따로 보내지 않는다. 인벤토리(S_InvList) 중
 * 표시명에 장착 접미사가 붙은 것만 골라 보여준다 (utils/equipment.ts 참조).
 * 그래서 "슬롯별 고정 칸"이 아니라 목록 형태다 — 어떤 부위인지는 서버가
 * 알려주지 않기 때문이다.
 */

import Phaser from 'phaser';
import { InventoryItem } from './Inventory';

/** 목록 한 줄의 높이. 11px 글자가 들어가는 최소치까지 좁혀 줄 수를 늘렸다. */
const ROW_HEIGHT = 18;
/**
 * 한 번에 보여줄 줄 수. 장착 부위는 무기·방어구·장신구를 합쳐 13개쯤 되므로
 * 대개 이 안에 다 들어가고, 넘치면 휠 스크롤로 마저 본다.
 */
const MAX_ROWS = 11;

/** 패널 높이. 좌하단 상시 HUD(CharacterStatsPanel) 위까지만 쓴다. */
const PANEL_HEIGHT = 258;

export class EquipmentPanel extends Phaser.GameObjects.Container {
  private panelWidth = 210;
  private panelHeight = PANEL_HEIGHT;

  /** 목록 한 줄이 쓸 수 있는 최대 폭. 좌우 12px씩 여백을 뺀 값. */
  private rowMaxWidth = 210 - 24;

  private rowTexts: Phaser.GameObjects.Text[] = [];
  private emptyText!: Phaser.GameObjects.Text;
  /** 하단 표시줄. 목록이 넘칠 때 "보이는 범위 / 총 개수"를 알린다. */
  private statusText!: Phaser.GameObjects.Text;

  /** 지금 보여주고 있는 장착 아이템 전체. 스크롤은 이 배열의 구간만 옮긴다. */
  private equipped: InventoryItem[] = [];
  /** 스크롤 위치(줄 단위). */
  private scrollRow = 0;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.sceneInput = scene.input;
    scene.add.existing(this);

    this.setScrollFactor(0);

    this.createUI();
    this.registerScrollInput();
    this.setDepth(160);
    this.setVisible(false);
  }

  public getPanelWidth(): number {
    return this.panelWidth;
  }

  public getPanelHeight(): number {
    return this.panelHeight;
  }

  private createUI(): void {
    const bg = this.scene.add.rectangle(0, 0, this.panelWidth, this.panelHeight, 0x1a1a2e, 0.95);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(
      0,
      -this.panelHeight / 2 + 15,
      this.panelWidth,
      30,
      0x2a2a4e
    );
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    const title = this.scene.add.text(0, -this.panelHeight / 2 + 15, 'Equipment', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.add(title);

    const closeBtn = this.scene.add.text(this.panelWidth / 2 - 20, -this.panelHeight / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    closeBtn.on('pointerdown', () => {
      this.setVisible(false);
      this.emit('panelclose');
    });
    this.add(closeBtn);

    // 목록 줄을 미리 만들어 두고 내용만 갈아 끼운다.
    const left = -this.panelWidth / 2 + 12;
    let y = -this.panelHeight / 2 + 46;

    for (let i = 0; i < MAX_ROWS; i++) {
      // 줄바꿈(wordWrap)을 걸지 않는다 — 두 줄이 되면 아래 줄과 겹친다.
      // 긴 이름은 fitRowText()가 말줄임으로 잘라 한 줄에 맞춘다.
      const row = this.scene.add.text(left, y, '', {
        fontSize: '11px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
      });
      row.setOrigin(0, 0.5);
      row.setVisible(false);
      this.add(row);
      this.rowTexts.push(row);
      y += ROW_HEIGHT;
    }

    this.emptyText = this.scene.add.text(0, 0, '장착한 아이템이 없습니다', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.emptyText.setOrigin(0.5);
    this.add(this.emptyText);

    this.statusText = this.scene.add.text(0, this.panelHeight / 2 - 14, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);
  }

  /**
   * 휠 스크롤을 씬 input에 등록한다.
   *
   * 목록 줄이 패널 배경을 덮고 있어 오브젝트 단위 wheel로는 글자 위에서 굴린 휠을
   * 놓친다. 그래서 씬 전역에서 받고 포인터가 패널 안인지 직접 판정한다
   * (Inventory와 같은 방식).
   */
  private registerScrollInput(): void {
    const onWheel = (
      pointer: Phaser.Input.Pointer,
      _objs: unknown,
      _dx: number,
      dy: number
    ) => {
      if (!this.visible || !this.isPointerOverPanel(pointer)) return;
      this.setScrollRow(this.scrollRow + (dy > 0 ? 1 : -1));
    };

    this.sceneInput.on('wheel', onWheel);
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('wheel', onWheel);
    });
  }

  /** 포인터가 패널 사각형 안에 있는지. 패널은 scrollFactor 0이라 화면 좌표로 바로 비교한다. */
  private isPointerOverPanel(pointer: Phaser.Input.Pointer): boolean {
    return (
      Math.abs(pointer.x - this.x) <= this.panelWidth / 2 &&
      Math.abs(pointer.y - this.y) <= this.panelHeight / 2
    );
  }

  /** 스크롤 가능한 최대 줄. 0이면 전부 한 화면에 들어간다. */
  private getMaxScrollRow(): number {
    return Math.max(0, this.equipped.length - MAX_ROWS);
  }

  private setScrollRow(row: number): void {
    const clamped = Phaser.Math.Clamp(row, 0, this.getMaxScrollRow());
    if (clamped === this.scrollRow) return;
    this.scrollRow = clamped;
    this.renderRows();
  }

  /**
   * 인벤토리 전체를 받아 장착 중인 것만 추려 보여준다.
   * 호출부가 미리 거르지 않아도 되도록 필터링을 여기서 한다.
   */
  public setItems(items: InventoryItem[]): void {
    this.equipped = items.filter((item) => item.equipped);

    // 장비를 벗어 목록이 짧아졌으면 스크롤 위치도 되돌린다.
    this.scrollRow = Phaser.Math.Clamp(this.scrollRow, 0, this.getMaxScrollRow());
    this.renderRows();
  }

  /**
   * 현재 스크롤 구간을 줄에 그린다.
   *
   * 예전에는 앞의 MAX_ROWS개만 그리고 나머지를 "외 N개 더 장착 중" 한 줄로
   * 뭉갰다 — 무엇이 장착됐는지 확인할 방법이 없었다. 이제 줄 수를 늘리고
   * 넘치는 만큼은 휠로 넘겨 본다.
   */
  private renderRows(): void {
    this.emptyText.setVisible(this.equipped.length === 0);

    this.rowTexts.forEach((row, index) => {
      const item = this.equipped[this.scrollRow + index];
      if (!item) {
        row.setVisible(false);
        return;
      }

      const enchant = item.enchantLevel > 0 ? `+${item.enchantLevel} ` : '';
      const count = item.count > 1 ? ` x${item.count}` : '';
      this.fitRowText(row, `${enchant}${item.name}${count}`);
      row.setColor(item.identified ? '#ffffff' : '#888888');
      row.setVisible(true);
    });

    this.updateStatusText();
  }

  /**
   * 한 줄에 들어가도록 문자열을 말줄임한다.
   *
   * Phaser Text에는 말줄임 기능이 없어 실제로 그려진 폭(row.width)을 보고
   * 한 글자씩 줄인다. 줄 수가 십여 개뿐이고 목록이 바뀔 때만 도는 계산이다.
   */
  private fitRowText(row: Phaser.GameObjects.Text, text: string): void {
    row.setText(text);
    if (row.width <= this.rowMaxWidth) return;

    let cut = text;
    while (cut.length > 1 && row.width > this.rowMaxWidth) {
      cut = cut.slice(0, -1);
      row.setText(`${cut}…`);
    }
  }

  /** 하단 표시줄. 넘칠 때만 범위를 알리고, 다 보이면 총 개수만 적는다. */
  private updateStatusText(): void {
    const total = this.equipped.length;
    if (total === 0) {
      this.statusText.setText('');
      return;
    }

    if (this.getMaxScrollRow() === 0) {
      this.statusText.setText(`${total}개 장착 중`);
      return;
    }

    const first = this.scrollRow + 1;
    const last = Math.min(total, this.scrollRow + MAX_ROWS);
    this.statusText.setText(`${first}-${last} / ${total}개 (휠로 스크롤)`);
  }

  public toggle(): void {
    this.setVisible(!this.visible);
  }
}
